import type { FC, ReactNode } from 'react'

import { useId, useState } from 'react'
import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { formatFull, formatInput, formatShort, parseAmount } from '@/lib/money'
import { cn } from '@/lib/utils'
import { useSession } from '@/hooks/use-session'
import { MAX_BET, MIN_BET } from '@/constants/site'

interface GameHeaderProps {
    title: string
    subtitle: string
}

interface PanelProps {
    children: ReactNode
    className?: string
}

interface StatBoxProps {
    label: string
    value: ReactNode
    tone?: 'good' | 'bad'
}

interface BetInputProps {
    value: string
    onChange: (value: string) => void
    disabled?: boolean
}

export const GameHeader: FC<GameHeaderProps> = ({ title, subtitle }): ReactNode => (
    <div className='mb-5 flex flex-wrap items-center gap-x-4 gap-y-2'>
        <Link
            href='/'
            className='inline-flex h-10 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-[15px] font-semibold transition hover:border-primary/60'
        >
            <ArrowLeft className='size-4' aria-hidden='true' />
            Lobby
        </Link>

        <h1 className='font-heading text-2xl font-bold tracking-wide sm:text-[1.75rem]'>
            {title}
        </h1>

        <p className='text-muted-foreground'>
            {subtitle}
        </p>
    </div>
)

export const GameLayout: FC<{ panel: ReactNode, arena: ReactNode }> = ({ panel, arena }): ReactNode => (
    <div className='grid items-start gap-4 lg:grid-cols-[320px_minmax(0,1fr)]'>
        {panel}
        {arena}
    </div>
)

export const Panel: FC<PanelProps> = ({ children, className }): ReactNode => (
    <div className={cn('space-y-4 rounded-xl border border-border bg-card p-4 sm:p-[18px]', className)}>
        {children}
    </div>
)

export const Arena: FC<PanelProps> = ({ children, className }): ReactNode => (
    <div className={cn('relative min-h-[360px] overflow-hidden rounded-xl border border-border bg-[#1a100b] sm:min-h-[440px]', className)}>
        {children}
    </div>
)

export const StatBox: FC<StatBoxProps> = ({ label, value, tone }): ReactNode => (
    <div className='rounded-lg border border-border bg-surface px-3 py-2'>
        <p className='text-xs text-muted-foreground'>{label}</p>
        <p className={cn('text-[15px] font-bold tabular-nums', tone === 'good' && 'text-success', tone === 'bad' && 'text-danger')}>{value}</p>
    </div>
)

// The bet box: type 50k / 2.5m / 1b, or use ½, 2×, Min and Max.
export const BetInput: FC<BetInputProps> = ({ value, onChange, disabled }): ReactNode => {
    const { user } = useSession()
    const id = useId()
    const amount = parseAmount(value)
    const balance = user?.balance ?? 0
    const ceiling = user && balance >= MIN_BET ? Math.min(MAX_BET, balance) : MAX_BET

    const set = (next: number) => onChange(formatInput(Math.max(MIN_BET, Math.min(MAX_BET, Math.floor(next)))))

    const quick = [
        { label: '½', title: 'Halve the bet', run: () => set((amount ?? MIN_BET) / 2) },
        { label: '2×', title: 'Double the bet', run: () => set(Math.min(ceiling, (amount ?? MIN_BET) * 2)) },
        { label: 'Min', title: 'Minimum bet', run: () => set(MIN_BET) },
        { label: 'Max', title: 'Maximum bet', run: () => set(ceiling) }
    ]

    let hint: ReactNode = <span>&nbsp;</span>

    if (!value.trim()) hint = <span className='text-danger'>Enter a bet</span>
    else if (amount === null) hint = <span className='text-danger'>Type an amount like 50k, 2.5m or 1b</span>
    else if (amount < MIN_BET || amount > MAX_BET) hint = <span className='text-danger'>Bets go from {formatShort(MIN_BET)} to {formatShort(MAX_BET)}</span>
    else hint = <span>= {formatFull(amount)}</span>

    return (
        <div>
            <label htmlFor={id} className='mb-1.5 block text-sm font-semibold text-foreground/90'>
                Bet amount
            </label>

            <div className={cn('flex h-12 overflow-hidden rounded-lg border border-input bg-surface transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/25', disabled && 'opacity-60')}>
                <span className='flex items-center pl-3 font-bold text-gold' aria-hidden='true'>$</span>

                <input
                    id={id}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    disabled={disabled}
                    inputMode='decimal'
                    autoComplete='off'
                    spellCheck={false}
                    className='min-w-0 flex-1 bg-transparent px-2 font-bold outline-none'
                    aria-describedby={`${id}-hint`}
                />

                {quick.map((button) => (
                    <button
                        key={button.label}
                        type='button'
                        title={button.title}
                        onClick={button.run}
                        disabled={disabled}
                        className='border-l border-border px-2.5 text-xs font-bold text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:pointer-events-none sm:px-3'
                    >
                        {button.label}
                    </button>
                ))}
            </div>

            <p id={`${id}-hint`} className='mt-1.5 text-xs text-muted-foreground'>
                {hint}
            </p>
        </div>
    )
}

// Shared bet-box state for each game page.
export function useStake(initial = '10k') {
    const [text, setText] = useState(initial)
    const amount = parseAmount(text)
    const valid = amount !== null && amount >= MIN_BET && amount <= MAX_BET

    return { text, setText, amount: valid ? amount : null }
}
